import { React, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Form, Formik } from 'formik';
import * as Yup from 'yup';

import { ReactComponent as DiplomaIcon } from '../../../../../assets/images/DiplomaIcon.svg';
import { ReactComponent as EducationIcon } from '../../../../../assets/images/EducationIcon.svg';
import { ReactComponent as UniversityIcon } from '../../../../../assets/images/UniversityIcon.svg';
import Button from '../../../../../components/shared/Button/Button';
import CustomTextField from '../../../../../components/shared/CustomTextField/CustomTextField';
import Modal from '../../../../../components/shared/Modal/Modal';
import StartEndYearFields from '../../../../../components/shared/StartEndYearFields/StartEndYearFields';
import { FORM_CONSTANTS, FORM_MESSAGES } from '../../../../../constants/commonConstants';
import { IsMobile } from '../../../../../helpers/IsMobile';

import styles from './AddEducationModal.module.scss';

const INITIAL_VALUES = {
  university: '',
  degree: '',
  field_study: '',
  start_year: '',
  end_year: '',
  description: '',
};

const AddEducationModal = ({ showModal, setShowModal, initialValues = {}, handleSave, formData }) => {
  const { t } = useTranslation('common');

  const EducationFormSchema = Yup.object().shape({
    university: Yup.string()
      .min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT)
      .max(FORM_CONSTANTS.MAX_LENGTH, FORM_MESSAGES.TOO_LONG)
      .required(t('validation.required')),
    degree: Yup.string()
      .min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT)
      .max(FORM_CONSTANTS.MAX_LENGTH, FORM_MESSAGES.TOO_LONG)
      .required(t('validation.required')),
    field_study: Yup.string()
      .min(FORM_CONSTANTS.MIN_LENGTH, FORM_MESSAGES.TOO_SHORT)
      .max(FORM_CONSTANTS.MAX_LENGTH, FORM_MESSAGES.TOO_LONG)
      .required(t('validation.required')),
    start_year: Yup.string()
      .min(FORM_CONSTANTS.YEAR_STRING_LENGTH)
      .max(FORM_CONSTANTS.YEAR_STRING_LENGTH)
      .required(t('validation.required')),
    end_year: Yup.string()
      .min(FORM_CONSTANTS.YEAR_STRING_LENGTH)
      .max(FORM_CONSTANTS.YEAR_STRING_LENGTH)
      .required(t('validation.required')),
  });

  const [formValues, setFormValues] = useState(INITIAL_VALUES);
  const [isEdit, setIsEdit] = useState(false);
  const modalFullScreen = IsMobile();

  useEffect(() => {
    if (Object.keys(initialValues).length) {
      setIsEdit(true);
      setFormValues(initialValues);
    } else {
      setFormValues({});
      setIsEdit(false);
    }
  }, [initialValues]);

  const handleSubmit = (data) => {
    const changedFormData = [...formData];
    const submitedValues = data;
    if (submitedValues.currentPosition) {
      submitedValues.endYear = '';
    }
    if (data !== initialValues) {
      const index = changedFormData.indexOf(initialValues);
      if (index > -1) {
        changedFormData.splice(initialValues, 1);
      }
      changedFormData.push(data);
      handleSave(changedFormData);
    } else {
      setShowModal(false);
    }
  };

  const handleDelete = (data) => {
    if (isEdit) {
      const newData = [...formData].map((education) =>
        data.id === education.id ? { ...education, _destroy: true } : education
      );
      handleSave(newData);
    } else {
      setShowModal(false);
    }
  };

  return (
    <Modal
      show={showModal}
      onHide={() => setShowModal(false)}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      fullscreen={modalFullScreen}
    >
      <Modal.CustomHeader closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          {isEdit ? t('profile.editEducation') : t('profile.addNewEducation')}
        </Modal.Title>
      </Modal.CustomHeader>
      <Modal.Body>
        <div className={modalFullScreen ? styles.phoneBody : styles.body}>
          <Formik
            initialValues={formValues}
            onSubmit={handleSubmit}
            enableReinitialize
            validationSchema={EducationFormSchema}
          >
            {({ values, dirty, isValid }) => (
              <Form>
                <div className={modalFullScreen && styles.phoneContainer}>
                  <div>
                    <Field
                      name="university"
                      as={CustomTextField}
                      type="text"
                      label={t('profile.schoolOrUniversity')}
                      className={styles.inputField}
                      CustomIcon={UniversityIcon}
                    />
                    <Field
                      name="degree"
                      as={CustomTextField}
                      type="text"
                      label={t('profile.degree')}
                      className={styles.inputField}
                      CustomIcon={EducationIcon}
                    />
                    <Field
                      name="field_study"
                      as={CustomTextField}
                      type="text"
                      label={t('profile.fieldOfStudy')}
                      className={`${styles.inputField} ${styles.noMarginBottom}`}
                      CustomIcon={DiplomaIcon}
                    />
                    <StartEndYearFields />
                  </div>
                  <div>
                    <div className={modalFullScreen ? styles.phoneButtonContainer : styles.buttonContainer}>
                      <Button
                        variant="secondary"
                        className={modalFullScreen ? styles.phoneButton : styles.button}
                        onClick={() => handleDelete(values)}
                      >
                        {isEdit ? t('delete') : t('cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        type="submit"
                        className={modalFullScreen ? styles.phoneButton : styles.button}
                        disabled={!(isValid && dirty)}
                      >
                        {t('save')}
                      </Button>
                    </div>
                  </div>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default AddEducationModal;
